// Notification-center store (NEX-296 / NEX-299 item 5; operator-notifications U2). The
// bell's two feeds, ONE badge:
//   · run notifications — the WS-event feed (lib/ws/notification-feed.ts), pushed here by
//     the WsEventBridge; in memory, capped, an unread counter;
//   · operator alerts — what the alert INBOX holds (operator-bff `GET /v1/alerts`, 24 h,
//     brief §7.4) merged with what arrived live into U1's store (lib/alerts/alerts.store.ts).
// Module-scoped Zustand store — mirrors connection.store.ts.
//
// The inbox is read ONCE per bell opening and once when the socket first connects (the
// badge then counts what the server holds unread) — never on a timer (frontend-conventions
// §8.1 / §15.10). Reading is the server's CURSOR (`POST /v1/alerts/read { upTo }`, through
// U1's `acknowledge`, so the other tabs hear it too); a click on one alert is a LOCAL read
// of that alert only — the BFF keeps no per-item read.

import { create } from 'zustand';
import type { QueryClient } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import type { Notification, NotificationDraft } from '@/lib/ws/notification-feed';
import { getAlerts } from '@/lib/alerts/alerts.api';
import {
  isOperatorAlertV1,
  type IOperatorAlertV1,
  type OperatorAlertKind,
} from '@/lib/alerts/alert.types';
import { alertView } from '@/lib/alerts/alert.view';
import {
  acknowledge,
  LIST_CAP,
  markRead,
  useAlertsStore,
  type IAlertsState,
} from '@/lib/alerts/alerts.store';

/** Cap the in-memory feed — a hub closure can fan a lot of events; the bell only
 * ever shows the most recent. Older entries drop off the tail. */
const MAX_NOTIFICATIONS = 50;
/** The inbox's retention (brief §7.4): the bell reads all of it, newest first. */
export const INBOX_WINDOW_MS = 24 * 3_600_000;
/** Per-item reads remembered (bounded, like U1's render-once memory). */
const READ_CAP = 200;

/** The kinds this build can say; a newer BFF's kind is left out, as U1 does. */
const KNOWN: Readonly<Record<OperatorAlertKind, true>> = {
  FLIGHT_DELAYED: true,
  FLIGHT_DELAY_CHANGED: true,
  FLIGHT_CANCELLED: true,
  FLIGHT_DIVERTED: true,
  DISRUPTION_OPENED: true,
};

export interface IInbox {
  status: 'idle' | 'loading' | 'error' | 'success';
  /** The last page read, newest first. */
  items: IOperatorAlertV1[];
  /** The server's read cursor at that read. */
  readUpTo: string | null;
  /** Unread the server counted BEYOND the page (all older than its last item). */
  beyond: number;
  /** The page's oldest `at` (ms): once the cursor passes it, `beyond` is read too. */
  floor: number;
}

export interface NotificationsState {
  notifications: Notification[];
  unread: number;
  inbox: IInbox;
  /** Alerts read one by one (clicked) — local only. */
  readIds: string[];
  push: (draft: NotificationDraft) => Notification;
  markAllRead: () => void;
  clear: () => void;
}

const EMPTY_INBOX: IInbox = {
  status: 'idle',
  items: [],
  readUpTo: null,
  beyond: 0,
  floor: Infinity,
};

let seq = 0;
let inflight: Promise<boolean> | null = null;

export const useNotificationsStore = create<NotificationsState>((set) => ({
  notifications: [],
  unread: 0,
  inbox: EMPTY_INBOX,
  readIds: [],

  push: (draft) => {
    seq += 1;
    const notification: Notification = {
      ...draft,
      id: `n${seq}`,
      at: Date.now(),
    };
    set((s) => {
      // A draft with a dedupeKey REPLACES its previous entry instead of stacking a
      // new one — a bulk run pushes progress per PNR and every page load replays the
      // terminal snapshot; without this the bell filled with dozens of identical
      // "Disruption resolved" rows and the unread badge climbed on nothing new.
      const dup = draft.dedupeKey
        ? s.notifications.findIndex((n) => n.dedupeKey === draft.dedupeKey)
        : -1;
      if (dup >= 0) {
        const next = s.notifications.slice();
        next[dup] = { ...notification, id: next[dup]!.id, at: next[dup]!.at };
        return { notifications: next };
      }
      return {
        notifications: [notification, ...s.notifications].slice(0, MAX_NOTIFICATIONS),
        unread: s.unread + 1,
      };
    });
    return notification;
  },

  /** The run notifications only — alerts are read through the cursor (`readAlertsShown`). */
  markAllRead: () => set({ unread: 0 }),
  clear: () => {
    inflight = null;
    set({ notifications: [], unread: 0, inbox: EMPTY_INBOX, readIds: [] });
  },
}));

/* ── the operator alerts the bell lists ── */

const ms = (iso: string | null | undefined): number => Date.parse(iso ?? '') || -Infinity;
const own = (a: IOperatorAlertV1, me?: string): boolean => !!me && a.actorUrn === me;

export interface IBellAlert {
  alert: IOperatorAlertV1;
  unread: boolean;
}

type InboxSlice = Pick<NotificationsState, 'inbox' | 'readIds'>;
type LiveSlice = Pick<IAlertsState, 'entries' | 'readUpTo'>;

/** The cursor both sides agree on: the server's (as last read) or this console's. */
const cursorOf = (n: InboxSlice, a: LiveSlice): number =>
  Math.max(ms(n.inbox.readUpTo), ms(a.readUpTo));

/**
 * The inbox page ∪ the live arrivals, deduped by id (brief §6 inv. 4), newest first,
 * capped. Unread = newer than the cursor, not the operator's own action, not clicked, and
 * not already consumed live (U1 folds the news about the disruption on screen into it).
 */
export function bellAlerts(n: InboxSlice, a: LiveSlice, me?: string): IBellAlert[] {
  const cursor = cursorOf(n, a);
  const live = new Map(a.entries.map((e) => [e.alert.id, e.unread]));
  const byId = new Map<string, IOperatorAlertV1>();
  for (const alert of [...a.entries.map((e) => e.alert), ...n.inbox.items]) {
    if (KNOWN[alert.kind] === true && !byId.has(alert.id)) byId.set(alert.id, alert);
  }
  return [...byId.values()]
    .sort((x, y) => ms(y.at) - ms(x.at))
    .slice(0, LIST_CAP)
    .map((alert) => ({
      alert,
      unread:
        live.get(alert.id) !== false &&
        ms(alert.at) > cursor &&
        !own(alert, me) &&
        !n.readIds.includes(alert.id),
    }));
}

/** The alerts' share of the badge: the unread listed + the server's beyond the page. */
export function bellAlertsUnread(list: readonly IBellAlert[], n: InboxSlice, a: LiveSlice): number {
  const beyond = cursorOf(n, a) >= n.inbox.floor ? 0 : n.inbox.beyond;
  return list.filter((x) => x.unread).length + beyond;
}

/** Where an alert leads — U1's targets: «Operar» for a disruption, else «Ver vuelo». */
export function alertTarget(alert: IOperatorAlertV1): string | null {
  const v = alertView(alert);
  return v.operate ?? v.flight;
}

async function readInbox(me: string | undefined, qc: QueryClient): Promise<boolean> {
  useNotificationsStore.setState((s) => ({ inbox: { ...s.inbox, status: 'loading' } }));
  try {
    const since = new Date(Date.now() - INBOX_WINDOW_MS).toISOString();
    const page = await qc.fetchQuery({
      queryKey: ['alerts', 'inbox'],
      queryFn: () => getAlerts(since, LIST_CAP),
      staleTime: 0,
      gcTime: 0,
      // ONE read per ask: a failure shows the bell's error + «Reintentar».
      retry: false,
    });
    const items = (page.items ?? []).filter(isOperatorAlertV1);
    const read = ms(page.readUpTo);
    // The server counts the whole retained scope, minus own; what it counted that this
    // page does not list is older than all of it.
    const listed = items.filter((x) => ms(x.at) > read && !own(x, me)).length;
    useNotificationsStore.setState({
      inbox: {
        status: 'success',
        items,
        readUpTo: page.readUpTo ?? null,
        beyond: Math.max(0, (page.unread ?? 0) - listed),
        floor: items.length ? Math.min(...items.map((x) => ms(x.at))) : Date.now(),
      },
    });
    // Read elsewhere (another device): the live side learns the cursor too.
    if (page.readUpTo) markRead(page.readUpTo);
    return true;
  } catch {
    useNotificationsStore.setState((s) => ({ inbox: { ...s.inbox, status: 'error' } }));
    return false;
  }
}

/** ONE read of the inbox; a read already in flight answers the next ask (never two). */
export function loadInbox(me?: string, qc: QueryClient = queryClient): Promise<boolean> {
  inflight ??= readInbox(me, qc).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** The socket's first connect: seed the badge from the inbox, once per page. */
export function seedInbox(me?: string, qc: QueryClient = queryClient): void {
  if (useNotificationsStore.getState().inbox.status === 'idle') void loadInbox(me, qc);
}

/**
 * The bell was opened (after its read) or «Marcar todo como leído»: the cursor moves to
 * the newest alert shown — here, on the BFF and in the other tabs (U1's `acknowledge`).
 * Nothing unread → nothing sent. Returns the ids that were unread.
 */
export function readAlertsShown(me?: string): string[] {
  const n = useNotificationsStore.getState();
  const a = useAlertsStore.getState();
  const list = bellAlerts(n, a, me);
  if (!bellAlertsUnread(list, n, a)) return [];
  acknowledge(list.map((x) => x.alert));
  return list.filter((x) => x.unread).map((x) => x.alert.id);
}

/** Per-item read: the one alert clicked (local — the BFF's read is a cursor). */
export function readAlert(id: string): void {
  useNotificationsStore.setState((s) =>
    s.readIds.includes(id) ? s : { readIds: [...s.readIds, id].slice(-READ_CAP) },
  );
}
